import { css } from '@emotion/core';

import { boxShadowGrey } from 'src/design-system/mixins';
import { Colors, Theme } from 'src/design-system/theme';

import type { Variant } from './CardSelectable';

const container = ({
  fullWidth,
  fullHeight,
  disabled,
}: {
  fullWidth: boolean;
  fullHeight: boolean;
  disabled: boolean;
}) => css`
  width: ${fullWidth ? '100%' : 'auto'};
  display: flex;
  flex-direction: column;
  padding: 0;
  ${fullHeight &&
  css`
    flex: 1;
    height: 100%;
  `}
  ${disabled &&
  css`
    opacity: 0.5;
  `}
`;

const boxShadow =
  ({ selected, variant, noShadow }: { selected: boolean; variant: Variant; noShadow: boolean }) =>
  (theme: Theme) => {
    if (selected && variant !== 'accent') {
      return css`
        box-shadow: 0 0 4px 0 ${theme.colors.accent};
      `;
    }

    if (noShadow) {
      return css`
        box-shadow: none;
      `;
    }
  };

const borderColor =
  ({ selected, borderColor }: { selected: boolean; borderColor: Colors }) =>
  (theme: Theme) => {
    if (selected) {
      return css`
        border-color: ${theme.colors.accent};
      `;
    }
    return css`
      border-color: ${theme.colors[borderColor]};
    `;
  };

const background =
  ({ selected, variant }: { selected: boolean; variant: Variant }) =>
  (theme: Theme) => {
    if (selected && variant === 'accent') {
      return css`
        background-color: ${theme.colors.accent};
      `;
    }
  };

const hover =
  ({ noHighlight }: { noHighlight: boolean }) =>
  (theme: Theme) =>
    css`
      ${noHighlight
        ? boxShadowGrey(1)
        : `border-color: ${theme.colors.accent};
        box-shadow: 0 0 4px 0 ${theme.colors.accent};
      `}
    `;

const card =
  (props: {
    selected: boolean;
    variant: Variant;
    noShadow: boolean;
    borderColor: Colors;
    hoverable: boolean;
    noHighlight: boolean;
    disabled: boolean;
  }) =>
  (theme: Theme) => {
    return css`
      cursor: ${props.disabled || !props.hoverable ? 'default' : 'pointer'};
      user-select: none;
      border-width: 1px;
      border-style: solid;
      ${borderColor(props)(theme)}
      ${background(props)(theme)}
      ${boxShadow(props)(theme)};
      position: relative;

      ${props.hoverable &&
      `
        &:hover {
          ${!props.selected && !props.disabled && hover(props)(theme)}
        }
      `}
    `;
  };

const iconForLightVariant = (theme: Theme) => css`
  background-color: ${theme.colors.light};
  top: -8px;
  right: -8px;
  border: 1px solid ${theme.colors.light};

  svg {
    color: ${theme.fontColors.accent};
    fill: ${theme.fontColors.accent};
  }
`;

const iconForAccenVariant = (theme: Theme) => css`
  top: 8px;
  right: 8px;

  svg {
    color: ${theme.fontColors.light};
    fill: ${theme.fontColors.light};
  }
`;

const icon =
  ({ variant }: { variant: Variant }) =>
  (theme: Theme) => {
    return css`
      position: absolute;
      height: 16px;
      width: 16px;
      border-radius: 50%;
      box-sizing: content-box;
      ${variant === 'light' && iconForLightVariant(theme)}
      ${variant === 'accent' && iconForAccenVariant(theme)}
    `;
  };

export const style = { card, icon, container };
